import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Refine, Authenticated, useGetIdentity } from '@refinedev/core';
import routerProvider from '@refinedev/react-router-v6';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/ko';
import { compositeAuthProvider } from './providers/compositeAuthProvider';
import { dataProvider } from './providers/dataProvider';
import { LoadingSpinner } from './components/LoadingSpinner';
import { TopNavLayoutEnhanced } from './components/layout/TopNavLayoutEnhanced';
import { PasswordChangeNotification } from './components/common/PasswordChangeNotification';
import { PasswordExpiryNotification } from './components/common/PasswordExpiryNotification';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { PermissionProvider } from './providers/PermissionProvider';
import { authService } from './services/auth.service';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// 파트너 사용자는 /status, 그 외는 /dashboard로 리다이렉트
const RootRedirect: React.FC = () => {
  const user = authService.getStoredUser();
  if (user?.userType === 'PARTNER') {
    return <Navigate to="/status" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

// Lazy load components for performance
const MonitoringDashboard = lazy(() =>
  import('./pages/edi/MonitoringDashboard').then(module => ({
    default: module.MonitoringDashboard,
  }))
);

const MonitoringLogin = lazy(() =>
  import('./pages/MonitoringLogin').then(module => ({
    default: module.MonitoringLogin,
  }))
);

const MainLogin = lazy(() =>
  import('./pages/auth/Login').then(module => ({
    default: module.Login,
  }))
);

const FindId = lazy(() =>
  import('./pages/auth/FindId').then(module => ({
    default: module.FindId,
  }))
);

const ForgotPassword = lazy(() =>
  import('./pages/auth/ForgotPassword').then(module => ({
    default: module.ForgotPassword,
  }))
);

const ResetPassword = lazy(() =>
  import('./pages/auth/ResetPassword').then(module => ({
    default: module.ResetPassword,
  }))
);

const ChangePassword = lazy(() =>
  import('./pages/auth/ChangePassword').then(module => ({
    default: module.ChangePassword,
  }))
);

const Unauthorized = lazy(() =>
  import('./pages/auth/Unauthorized').then(module => ({
    default: module.Unauthorized,
  }))
);

const DashboardGuard = lazy(() =>
  import('./components/common/DashboardGuard').then(module => ({
    default: module.DashboardGuard,
  }))
);

// Main menu pages
const DispatchPage = lazy(() =>
  import('./pages/dispatch/DispatchPage').then(module => ({
    default: module.DispatchPage,
  }))
);

const DispatchCreatePage = lazy(() =>
  import('./pages/dispatch/DispatchCreatePage').then(module => ({
    default: module.DispatchCreatePage,
  }))
);

const DispatchEditPage = lazy(() =>
  import('./pages/dispatch/DispatchEditPage').then(module => ({
    default: module.DispatchEditPage,
  }))
);

const DispatchDetailPage = lazy(() =>
  import('./pages/dispatch/DispatchDetailPage').then(module => ({
    default: module.DispatchDetailPage,
  }))
);

const PickupManagementListPage = lazy(() =>
  import('./pages/pickup/PickupManagementListPage').then(module => ({
    default: module.PickupManagementListPage,
  }))
);

const PickupDetailPage = lazy(() =>
  import('./pages/pickup/PickupDetailPage').then(module => ({
    default: module.PickupDetailPage,
  }))
);

const DeliveryPage = lazy(() =>
  import('./pages/delivery/DeliveryPage').then(module => ({
    default: module.DeliveryPage,
  }))
);

const DeliveryDetailPage = lazy(() =>
  import('./pages/delivery/DeliveryDetailPage').then(module => ({
    default: module.DeliveryDetailPage,
  }))
);

const TransmissionListPage = lazy(() =>
  import('./pages/transmission/TransmissionManagementListPage').then(module => ({
    default: module.TransmissionManagementListPage,
  }))
);

const EpodRegisterPage = lazy(() =>
  import('./pages/transmission/EpodRegisterPage').then(module => ({
    default: module.EpodRegisterPage,
  }))
);

const EpodPodDetailPage = lazy(() =>
  import('./pages/transmission/EpodPodDetailPage').then(module => ({
    default: module.EpodPodDetailPage,
  }))
);

const TransmissionDetailPage = lazy(() =>
  import('./pages/transmission/TransmissionDetailPage').then(module => ({
    default: module.TransmissionDetailPage,
  }))
);

const EdocListPage = lazy(() =>
  import('./pages/transmission/edoc/EdocListPage').then(module => ({
    default: module.EdocListPage,
  }))
);

const TransferPage = lazy(() =>
  import('./pages/transfer/TransferPage').then(module => ({
    default: module.TransferPage,
  }))
);

const StatusPage = lazy(() =>
  import('./pages/status/StatusListPage').then(module => ({
    default: module.StatusListPage,
  }))
);

const StatusDetailPage = lazy(() =>
  import('./pages/status/StatusDetailPage').then(module => ({
    default: module.StatusDetailPage,
  }))
);

const NoticePage = lazy(() =>
  import('./pages/notice/NoticePage').then(module => ({
    default: module.NoticePage,
  }))
);

const NoticeCreatePage = lazy(() =>
  import('./pages/notice/NoticeCreatePage').then(module => ({
    default: module.NoticeCreatePage,
  }))
);

const NoticeDetailPage = lazy(() =>
  import('./pages/notice/NoticeDetailPage').then(module => ({
    default: module.NoticeDetailPage,
  }))
);

const NoticeEditPage = lazy(() =>
  import('./pages/notice/NoticeEditPage').then(module => ({
    default: module.NoticeEditPage,
  }))
);

// System management pages
const SystemPage = lazy(() =>
  import('./pages/system/SystemPage').then(module => ({
    default: module.SystemPage,
  }))
);

const PermissionsPage = lazy(() =>
  import('./pages/system/permissions/PermissionsPage').then(module => ({
    default: module.PermissionsPage,
  }))
);

const AddressesPage = lazy(() =>
  import('./pages/system/addresses/AddressesPage').then(module => ({
    default: module.AddressesPage,
  }))
);

const UsersPage = lazy(() =>
  import('./pages/system/users/UsersPage').then(module => ({
    default: module.UsersPage,
  }))
);

const PushNotificationsPage = lazy(() =>
  import('./pages/system/push-notifications/PushNotificationsPage').then(module => ({
    default: module.PushNotificationsPage,
  }))
);

const AppVersionsPage = lazy(() =>
  import('./pages/system/app-versions/AppVersionsPage').then(module => ({
    default: module.AppVersionsPage,
  }))
);

const RegionsPage = lazy(() =>
  import('./pages/system/regions/RegionsPage').then(module => ({
    default: module.RegionsPage,
  }))
);

const DevPage = lazy(() =>
  import('./pages/system/dev/DevPage').then(module => ({
    default: module.DevPage,
  }))
);

const LogsPage = lazy(() =>
  import('./pages/system/logs/LogsPage').then(module => ({
    default: module.LogsPage,
  }))
);

const BackupListPage = lazy(() =>
  import('./pages/system/backups/BackupListPage').then(module => ({
    default: module.BackupListPage,
  }))
);

const ArrivalTimePage = lazy(() =>
  import('./pages/system/arrival-time/ArrivalTimePage').then(module => ({
    default: module.ArrivalTimePage,
  }))
);

const SettlementPage = lazy(() =>
  import('./pages/settlement/SettlementPage').then(module => ({
    default: module.SettlementPage,
  }))
);

const ProfilePage = lazy(() =>
  import('./pages/profile/ProfilePage').then(module => ({
    default: module.ProfilePage,
  }))
);

const SettingsPage = lazy(() =>
  import('./pages/settings/SettingsPage').then(module => ({
    default: module.SettingsPage,
  }))
);

const DownloadPage = lazy(() =>
  import('./pages/downloadPage').then(module => ({
    default: module.DownloadPage,
  }))
);

const UpdatePage = lazy(() =>
  import('./pages/updatePage').then(module => ({
    default: module.UpdatePage,
  }))
);

/**
 * Protected Layout Component
 * - Uses Refine's useGetIdentity to get user data from authProvider
 * - Maps identity.fullName to userInfo.full_name for header display
 */
const ProtectedLayout: React.FC = () => {
  const { data: identity } = useGetIdentity<{
    id: string;
    name: string;
    email: string;
    fullName?: string;
  }>();

  return (
    <PermissionProvider>
      <TopNavLayoutEnhanced
        userInfo={{
          name: identity?.name || '이드림',
          full_name: identity?.fullName || identity?.name || '이드림',
          role: '사용자',
        }}
      >
        <Outlet />
      </TopNavLayoutEnhanced>
    </PermissionProvider>
  );
};

export const App: React.FC = () => {
  const [showPasswordChangeNotification, setShowPasswordChangeNotification] = useState(false);
  const [showPasswordExpiryNotification, setShowPasswordExpiryNotification] = useState(false);

  useEffect(() => {
    // Listen for password change notification event (임시 비밀번호)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dream_show_password_change_notification' && e.newValue === 'true') {
        setShowPasswordChangeNotification(true);
        localStorage.removeItem('dream_show_password_change_notification');
      }
      if (e.key === 'dream_show_password_expiry_notification' && e.newValue === 'true') {
        setShowPasswordExpiryNotification(true);
        localStorage.removeItem('dream_show_password_expiry_notification');
      }
    };

    // Check on mount
    if (localStorage.getItem('dream_show_password_change_notification') === 'true') {
      setShowPasswordChangeNotification(true);
      localStorage.removeItem('dream_show_password_change_notification');
    }
    if (localStorage.getItem('dream_show_password_expiry_notification') === 'true') {
      setShowPasswordExpiryNotification(true);
      localStorage.removeItem('dream_show_password_expiry_notification');
    }

    window.addEventListener('storage', handleStorageChange);

    // Custom event for same-window communication (임시 비밀번호)
    const handlePasswordChangeEvent = () => {
      if (localStorage.getItem('dream_show_password_change_notification') === 'true') {
        setShowPasswordChangeNotification(true);
        localStorage.removeItem('dream_show_password_change_notification');
      }
    };

    // Custom event for same-window communication (90일 만료)
    const handlePasswordExpiryEvent = () => {
      if (localStorage.getItem('dream_show_password_expiry_notification') === 'true') {
        setShowPasswordExpiryNotification(true);
        localStorage.removeItem('dream_show_password_expiry_notification');
      }
    };

    window.addEventListener('dream-password-notification', handlePasswordChangeEvent);
    window.addEventListener('dream-password-expiry-notification', handlePasswordExpiryEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('dream-password-notification', handlePasswordChangeEvent);
      window.removeEventListener('dream-password-expiry-notification', handlePasswordExpiryEvent);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
        <CssBaseline />
        <GlobalStyles
          styles={{
            html: {
              width: '100%',
              maxWidth: '100vw',
              height: '100vh',
              overflow: 'hidden',
              margin: 0,
              padding: 0,
            },
            body: {
              width: '100%',
              maxWidth: '100vw',
              height: '100vh',
              overflow: 'hidden',
              margin: 0,
              padding: 0,
            },
            '#root': {
              width: '100%',
              maxWidth: '100vw',
              height: '100vh',
              overflow: 'hidden',
            },
          }}
        />

        <BrowserRouter>
          {/* Password Change Notification Dialog - 임시 비밀번호 */}
          <PasswordChangeNotification
            open={showPasswordChangeNotification}
            onClose={() => setShowPasswordChangeNotification(false)}
          />

          {/* Password Expiry Notification Dialog - 90일 만료 */}
          <PasswordExpiryNotification
            open={showPasswordExpiryNotification}
            onClose={() => setShowPasswordExpiryNotification(false)}
          />

          <Refine
            authProvider={compositeAuthProvider}
            dataProvider={dataProvider}
            routerProvider={routerProvider}
            resources={[
              {
                name: 'dispatches',
                list: '/dispatch',
                create: '/dispatch/create',
                show: '/dispatch/:id',
                edit: '/dispatch/:id/edit',
                meta: {
                  label: '배차관리',
                },
              },
              {
                name: 'pickup-management',
                list: '/pickup',
                show: '/pickup/:id',
                meta: {
                  label: '픽업관리',
                },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: false,
            }}
          >
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Root route - redirect based on main auth and user type */}
                <Route
                  path="/"
                  element={
                    <Authenticated key="root-auth" fallback={<Navigate to="/login" replace />}>
                      <RootRedirect />
                    </Authenticated>
                  }
                />

                {/* Main system login - redirects to /dashboard if authenticated */}
                <Route
                  path="/login"
                  element={
                    <Authenticated key="main-login-auth" fallback={<MainLogin />}>
                      <Navigate to="/dashboard" replace />
                    </Authenticated>
                  }
                />
                <Route
                  path="/auth/find-id"
                  element={
                    <Authenticated key="auth-find-id" fallback={<FindId />}>
                      <Navigate to="/dashboard" replace />
                    </Authenticated>
                  }
                />
                <Route
                  path="/auth/forgot-password"
                  element={
                    <Authenticated key="auth-forgot-password" fallback={<ForgotPassword />}>
                      <Navigate to="/dashboard" replace />
                    </Authenticated>
                  }
                />
                <Route
                  path="/auth/reset-password"
                  element={
                    <Authenticated key="auth-reset-password" fallback={<ResetPassword />}>
                      <Navigate to="/dashboard" replace />
                    </Authenticated>
                  }
                />
                <Route
                  path="/auth/change-password"
                  element={
                    <Authenticated key="auth-change-password" fallback={<ChangePassword />}>
                      <Navigate to="/dashboard" replace />
                    </Authenticated>
                  }
                />

                {/* Unauthorized page - no authentication required */}
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Download page - public access, no authentication required */}
                <Route path="/download" element={<DownloadPage />} />

                {/* Update page - public access, no authentication required */}
                <Route path="/update" element={<UpdatePage />} />

                {/* Main system protected routes with enhanced layout */}
                <Route
                  element={
                    <Authenticated key="main-dashboard-auth" redirectOnFail="/login">
                      <ProtectedLayout />
                    </Authenticated>
                  }
                >
                  {/* Dashboard - 권한 체크 포함 */}
                  <Route path="/dashboard" element={<DashboardGuard />} />

                  {/* Main menu pages */}
                  <Route path="/dispatch" element={<DispatchPage />} />
                  <Route path="/dispatch/create" element={<DispatchCreatePage />} />
                  <Route path="/dispatch/:id" element={<DispatchDetailPage />} />
                  <Route path="/dispatch/:id/edit" element={<DispatchEditPage />} />
                  <Route path="/pickup" element={<PickupManagementListPage />} />
                  <Route path="/pickup/:id" element={<PickupDetailPage />} />
                  <Route path="/delivery" element={<DeliveryPage />} />
                  <Route path="/delivery/:id" element={<DeliveryDetailPage />} />
                  <Route path="/transmission" element={<TransmissionListPage />} />
                  <Route path="/transmission/detail" element={<TransmissionListPage />} />
                  <Route path="/transmission/epod-register" element={<EpodRegisterPage />} />
                  <Route path="/transmission/pod/:shipmentId" element={<EpodPodDetailPage />} />
                  <Route path="/transmission/:id" element={<TransmissionDetailPage />} />
                  <Route path="/transmission/edoc" element={<EdocListPage />} />
                  <Route path="/transfer" element={<TransferPage />} />
                  <Route path="/status" element={<StatusPage />} />
                  <Route path="/status/:id" element={<StatusDetailPage />} />
                  <Route path="/settlement" element={<SettlementPage />} />
                  <Route path="/notice" element={<NoticePage />} />
                  <Route path="/notice/create" element={<NoticeCreatePage />} />
                  <Route path="/notice/:id/edit" element={<NoticeEditPage />} />
                  <Route path="/notice/:id" element={<NoticeDetailPage />} />

                  {/* Profile page */}
                  <Route path="/profile" element={<ProfilePage />} />

                  {/* Settings page */}
                  <Route path="/settings" element={<SettingsPage />} />

                  {/* System management pages */}
                  <Route path="/system" element={<SystemPage />} />
                  <Route
                    path="/system/permissions"
                    element={
                      <ProtectedRoute
                        requiredPermissions={['ROLE_ADMIN']}
                        unauthorizedRedirect="/dashboard"
                      >
                        <PermissionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/system/addresses" element={<AddressesPage />} />
                  <Route path="/system/arrival-time" element={<ArrivalTimePage />} />
                  <Route path="/system/regions" element={<RegionsPage />} />
                  <Route
                    path="/system/users"
                    element={
                      <ProtectedRoute
                        requiredPermissions={['ROLE_ADMIN']}
                        unauthorizedRedirect="/dashboard"
                      >
                        <UsersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/system/push-notifications"
                    element={
                      <ProtectedRoute requiredPermissions={['ROLE_ADMIN']}>
                        <PushNotificationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/system/app-versions"
                    element={
                      <ProtectedRoute requiredPermissions={['ROLE_ADMIN']}>
                        <AppVersionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/system/logs"
                    element={
                      <ProtectedRoute requiredPermissions={['ROLE_ADMIN']}>
                        <LogsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/system/backups"
                    element={
                      <ProtectedRoute requiredPermissions={['ROLE_ADMIN']}>
                        <BackupListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/system/dev"
                    element={
                      <ProtectedRoute requiredPermissions={['ROLE_ADMIN']}>
                        <DevPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Monitoring system login - redirects to /monitoring if authenticated */}
                <Route
                  path="/monitoring/login"
                  element={
                    <Authenticated key="monitoring-login-auth" fallback={<MonitoringLogin />}>
                      <Navigate to="/monitoring" replace />
                    </Authenticated>
                  }
                />

                {/* Monitoring system protected route */}
                <Route
                  element={
                    <Authenticated
                      key="monitoring-dashboard-auth"
                      redirectOnFail="/monitoring/login"
                    >
                      <Outlet />
                    </Authenticated>
                  }
                >
                  <Route path="/monitoring" element={<MonitoringDashboard />} />
                </Route>

                {/* Catch all - redirect to root */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Refine>
        </BrowserRouter>
      </LocalizationProvider>
    </QueryClientProvider>
  );
};

export default App;
